import { Paginator as PrimePaginator } from 'primereact/paginator';
import { cloneElement, useId } from 'react';
import { DropdownField } from '@/shared/ui/DropdownField/DropdownField';
import type { PaginatorPageChangeEvent } from 'primereact/paginator';
import classes from './Paginator.module.scss';

interface PaginatorProps {
    first: number;
    rows: number;
    totalRecords: number;
    rowsPerPageOptions?: number[];
    disabled?: boolean;
    label?: string;
    onPageChange: (event: PaginatorPageChangeEvent) => void;
}

/** Показывает страницы списка, размер страницы и общее число записей */
export const Paginator = (
    { first, rows, totalRecords, rowsPerPageOptions = [10, 25, 50, 100], disabled = false, label = 'Страницы списка', onPageChange }: PaginatorProps
) => {
    const sizeId = useId();
    const total = Math.max(0, totalRecords);
    const size = Math.max(1, rows);
    const offset = Math.min(Math.max(0, first), Math.max(0, Math.ceil(total / size) - 1) * size);

    const sizes = [...new Set([...rowsPerPageOptions, size])].filter(value => value > 0)
        .sort((
            left,
            right
        ) => left - right);

    return <nav
        className={classes.paginator}
        aria-label={label}
    >
        <fieldset
            disabled={disabled}
            aria-label={label}
        >
            <PrimePaginator
                first={offset}
                rows={size}
                totalRecords={total}
                pageLinkSize={5}
                pt={{
                    firstPageButton: { 'aria-label': 'Первая страница' },
                    prevPageButton: { 'aria-label': 'Предыдущая страница' },
                    nextPageButton: { 'aria-label': 'Следующая страница' },
                    lastPageButton: { 'aria-label': 'Последняя страница' },
                }}
                onPageChange={event => {
                    if (!disabled) onPageChange({
                        first: event.first,
                        rows: event.rows,
                        page: event.page,
                        pageCount: Math.ceil(total / event.rows)
                    });
                }}
                template={{
                    layout: 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport',
                    PageLinks: options => cloneElement(options.element, { 'aria-label': `Страница ${options.page + 1}` }),
                    RowsPerPageDropdown: () => <div className={classes.pageSize}>
                        <span>На странице</span>
                        <DropdownField
                            id={`${sizeId}-control`}
                            name={sizeId}
                            label="Строк на странице"
                            placeholder="Строк на странице"
                            showLabel={false}
                            showClear={false}
                            disabled={disabled}
                            wrapperClassName={classes.sizePicker}
                            options={sizes.map(value => ({
                                id: value,
                                name: String(value)
                            }))}
                            value={size}
                            onChange={event => {
                                if (disabled || typeof event.value !== 'number') return;
                                const nextSize = event.value;

                                onPageChange({
                                    first: 0,
                                    rows: nextSize,
                                    page: 0,
                                    pageCount: Math.ceil(total / nextSize)
                                });
                            }}
                        />
                    </div>,
                    CurrentPageReport: () => <span
                        className={classes.report}
                        aria-live="polite"
                    >
                        {total
                            ? offset + 1
                            : 0}–{Math.min(offset + size, total)} из {total}
                    </span>
                }}
            />
        </fieldset>
    </nav>;
};
